import React from 'react';
import { getAvatarColors, getInitials } from '../tokens';

interface AvatarProps {
  name: string | null | undefined;
  src?: string | null;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  shape?: 'circle' | 'rounded';
  ring?: boolean;
  className?: string;
  /**
   * Optional seed for the deterministic color palette. When provided, the
   * background/text color is derived from this value instead of `name` — e.g.
   * pass an email address so two people with the same display name still get
   * distinct colors. Falls back to `name` when omitted (backward-compatible).
   */
  colorSeed?: string;
}

const SIZE = {
  xs: { box: 'size-6',  text: 'text-[10px]' },
  sm: { box: 'size-8',  text: 'text-xs'     },
  md: { box: 'size-10', text: 'text-sm'     },
  lg: { box: 'size-14', text: 'text-lg'     },
  xl: { box: 'size-20', text: 'text-2xl'    },
};

export const Avatar: React.FC<AvatarProps> = ({
  name,
  src,
  size = 'md',
  shape = 'circle',
  ring = false,
  className = '',
  colorSeed,
}) => {
  const colors = getAvatarColors(colorSeed ?? name ?? '');
  const initials = getInitials(name);
  const sz = SIZE[size];
  const radius = shape === 'circle' ? 'rounded-full' : 'rounded-lg';
  const ringClass = ring ? 'ring-2 ring-white dark:ring-slate-900' : '';

  if (src) {
    return (
      <img
        src={src}
        alt={name || ''}
        className={`${sz.box} ${radius} ${ringClass} object-cover flex-shrink-0 ${className}`}
      />
    );
  }

  return (
    <div
      className={[
        sz.box, sz.text, radius, ringClass, colors.bg, colors.text,
        'flex items-center justify-center font-bold flex-shrink-0 select-none',
        className,
      ].join(' ')}
      aria-label={name || 'avatar'}
    >
      {initials}
    </div>
  );
};
